import { X } from "@phosphor-icons/react";
import MarketingConsent from "components/common/MarketingConsent";
import ShopfloBadge from "components/common/ShopfloBadge";
import { CaptchaWrapper, CaptchaState, CaptchaStatus } from "components/common/CaptchaWrapper";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { DialogBody, DialogHeader, GenericDialog } from "components/common/dialogs/GenericDialog";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import CustomComponentSlot from "components/common/CustomComponentSlot";
import DPDPAConsent from "components/common/consent/DPDPAConsent";
import { useJsonPermission } from "lib/hooks/useJsonPermission";
import { useToken } from "lib/contexts/TokenProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { getRequest } from "lib/core/apiClient";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import useForm, { FormField } from "lib/hooks/useForm";
import useInterval from "lib/hooks/useInterval";
import { useLocale } from "lib/hooks/useLocale";
import { invokeTruecallerDeeplink } from "lib/third-party/truecaller";
import { CartDialogType } from "lib/types/cart";
import { OTPVerificationDetails, UserLoginType } from "lib/types/user";
import { OTPDeliveryType, sendOtpWithPhoneNumber } from "lib/utils/auth";
import { createCartToCheckoutURL, isCartRedirectEnabled } from "lib/utils/checkout";
import { analyticsEvents, analyticsTypes, constants, eventTypes } from "lib/utils/constants";
import {
  addDefaultCountryCode,
  classNames,
  getPhoneInputMaxLength,
  inIframe,
  isMobile,
  publishPostMessage,
} from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";
import React, { useCallback, useEffect, useState } from "react";
import PhoneInput, {
  Country,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  Value as PhoneInputValueType,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSearchParams } from "react-router-dom";

interface CartAuthenticationDialogProps {
  open: boolean;
  setDialog: (dialog: CartDialogType) => void;
  context?: "SSO" | "AUTH";
}

let isPhoneInputFirstFocus = false;
let pollCounter = 1;

const CartAuthenticationDialog: React.FC<CartAuthenticationDialogProps> = ({
  open,
  setDialog,
  context = "SSO",
}) => {
  const { t } = useLocale();
  const { cartPermissions } = useJsonPermission();
  const isCtaBlockedByRule = Boolean(cartPermissions.loginBtnStatus);

  const {
    actions: { login },
    state: { thirdPartyAuth },
  } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<Country | undefined>("IN");
  const {
    actions: { setUserLoginType, updateUser },
    state: { user },
  } = useUserContext();
  const {
    state: { originUrl, checkoutId, checkoutValidations, cartDialog, checkoutItems, appliedCoupons, billing },
    actions: { setCheckoutValidations },
  } = useCheckoutContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { tokenId },
  } = useToken();

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState<CaptchaState>({
    token: null,
    status: CaptchaStatus.LOADING,
  });
  const phoneNumber = searchParams.get("phoneNumber");
  const isOverlayClickDisabled = Boolean(searchParams.get("disable_cart_auth_overlay_click"));

  const [phoneInputFocused, setPhoneInputFocused] = useState<boolean>(false);
  const [isLoginStatusPolling, setIsLoginStatusPolling] = useState<boolean>(false);
  const [authRequestId, setAuthRequestId] = useState<string>("");

  const isCaptchaValidating = merchant?.automationEnabled
    ? false
    : captchaToken.status === CaptchaStatus.LOADING || !Boolean(captchaToken.token?.length);

  const {
    handleSubmit,
    inputProps,
    setValueOf,
    setErrors,
    handleFieldChange,
    state: { values, errors },
  } = useForm({
    initialState: { phoneNumber: phoneNumber ?? "" },
    validationSchema: {
      phoneNumber: {
        required: t("invalid_phone_number"),
        phone: t("invalid_phone_number"),
        formatters: ["PHONE"],
      },
    },
  });

  useEffect(() => {
    if (!Boolean(user?.phone)) return;
    setValueOf("phoneNumber", user?.phone);
  }, [user]);

  // Handles Phone number submission
  const handlePhoneNumberSubmit = async ({ phoneNumber }: { phoneNumber: string }) => {
    if (!(isPossiblePhoneNumber(phoneNumber) && isValidPhoneNumber(phoneNumber))) {
      errorToast(t("invalid_phone"));
      return;
    }
    try {
      setIsLoading(true);
      setErrors({ phoneNumber: { status: false }, otp: { status: false, showAlert: false } });
      setValueOf("otp", "");
      let verificationDetails: OTPVerificationDetails = {
        identifier: addDefaultCountryCode(phoneNumber),
        merchantId: merchant?.merchantId ?? "",
        domain: inIframe() ? originUrl : constants?.CHECKOUT_HOST,
      };

      let ssoRequestId;
      let contextMetada: OTPDeliveryType = {
        context: "AUTH",
      };
      if (context === "SSO") {
        ssoRequestId = searchParams.get("request-id");
        if (ssoRequestId) {
          contextMetada = {
            context: "SSO",
            requestId: ssoRequestId,
          };
        }
      }
      if (context !== "SSO" && merchant?.automationEnabled) {
        contextMetada = {
          context: "NO_RECAPTCHA",
        };
      }
      const result = await sendOtpWithPhoneNumber(
        verificationDetails,
        contextMetada,
        captchaToken.token ?? "",
        merchant?.captchaProvider ?? "TURNSTILE",
      );
      setIsLoading(false);
      if (!Boolean(result)) return;
      const isExistingUser: boolean = result.otp_required ?? result.existing_customer;
      // setContextId(result.context_id);
      searchParams.set("ctxId", result.context_id);
      searchParams.set("phoneNumber", phoneNumber);
      setSearchParams(searchParams);

      //Phone entered event
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_AUTH_PHONE_ENTERED,
        eventType: "click",
        metaData: {
          userType: isExistingUser ? "repeat" : "guest",
          phoneNumber: verificationDetails.identifier,
        },
        context: "cart",
      });

      if (!isExistingUser) {
        const authToken = result.token?.access_token;
        const expiry = result.token?.access_token_expires_at;
        login(authToken, "", expiry);
        updateUser();
        publishPostMessage(eventTypes.CART_TO_CHECKOUT, {
          checkoutId: checkoutId,
          lineItems: checkoutItems,
          cartTotal: billing.total_payable,
          coupons: appliedCoupons,
          tokenId,
        });
        setDialog(null);
        setUserLoginType(analyticsEvents.FLO_GUEST_LOGIN as UserLoginType);
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_GUEST_LOGIN,
          eventFor: [analyticsTypes.SF_ANALYTICS],
          eventType: "flo_action",
          metaData: {
            userData: {
              type: analyticsEvents.FLO_GUEST_LOGIN,
            },
            authSource: "SHOPFLO",
          },
          context: "cart",
        });
        if (isCartRedirectEnabled()) {
          const redirectUrl = createCartToCheckoutURL(document.location.href, checkoutId);
          window.location.href = redirectUrl.href;
          return;
        }
        return;
      }
      cartDialog === "cartAuthentication" ? setDialog("cartOtpVerification") : setDialog("otpVerification");
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_AUTH_OTP_LOADED,
        eventType: "load",
        context: "cart",
      });
    } catch (err: any) {
      setIsLoading(false);
      if (err?.response?.status === 400) {
        setErrors({
          phoneNumber: {
            status: true,
            message: t("invalid_phone_number"),
          },
        });
      } else {
        errorToast(t("otp_fetch_failed"));
      }
    } finally {
      setCaptchaToken((prev) => ({ ...prev, token: null }));
    }
  };

  const onPhoneInputFocus = () => {
    setPhoneInputFocused(true);
    if (Boolean(values?.phoneNumber?.length) || !thirdPartyAuth?.length || isPhoneInputFirstFocus) return;
    const truecallerRequest = thirdPartyAuth[0];
    if (!truecallerRequest) return;
    const authProvider = truecallerRequest.provider;

    switch (authProvider) {
      case "TRUECALLER": {
        setAuthRequestId(truecallerRequest.requestId);
        isPhoneInputFirstFocus = true;
        invokeTruecallerDeeplink(truecallerRequest.redirectUrl);
        setIsLoginStatusPolling(true);
        break;
      }
    }
  };

  const pollThirdPartyAuthStatus = useCallback(async () => {
    if (!authRequestId) {
      stopAuthPolling();
      return;
    }
    let statusResponse;
    try {
      statusResponse = await getRequest(`/auth-callback/${authRequestId}/status`, "AUTH");
      if (statusResponse.status === "VERIFIED") {
        stopAuthPolling();
        const authToken = statusResponse?.metadata?.token_response?.access_token;
        const authTokenExpiry = statusResponse?.metadata?.token_response?.access_token_expires_at;
        const refreshToken = statusResponse?.metadata?.token_response?.refresh_token;
        const refreshTokenExpiry = statusResponse?.metadata?.token_response?.refresh_token_expires_at;
        const isExistingUser = statusResponse?.metadata?.token_response?.existing_user;
        login(authToken, refreshToken, authTokenExpiry, refreshTokenExpiry);
        updateUser();
        setUserLoginType(
          isExistingUser
            ? (analyticsEvents.FLO_RETURN_USER_LOGIN as UserLoginType)
            : (analyticsEvents.FLO_GUEST_LOGIN as UserLoginType),
        );
        sendAnalyticsEvent({
          eventName: isExistingUser ? analyticsEvents.FLO_RETURN_USER_LOGIN : analyticsEvents.FLO_GUEST_LOGIN,
          eventType: "flo_action",
          metaData: {
            userData: {
              type: isExistingUser ? analyticsEvents.FLO_RETURN_USER_LOGIN : analyticsEvents.FLO_GUEST_LOGIN,
            },
            authSource: "TRUECALLER",
          },
          context: "cart",
        });

        return;
      }
      if (statusResponse.status === "FAILED" || statusResponse.status === "USER_REJECTED") {
        stopAuthPolling();
        return;
      }
      if (pollCounter >= 30) {
        stopAuthPolling();
        return;
      }
      pollCounter++;
    } catch (e) {
      console.error(e);
      stopAuthPolling();
    } finally {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_TRUECALLER_ACTION,
        eventType: "flo_action",
        metaData: {
          action: {
            type: statusResponse.status,
          },
        },
        context: "cart",
      });
    }
  }, [authRequestId]);

  const stopAuthPolling = () => {
    pollCounter = 1;
    setIsLoginStatusPolling(false);
  };

  useInterval(pollThirdPartyAuthStatus, isLoginStatusPolling ? 1500 : null);

  return (
    <GenericDialog
      isOpen={open}
      setIsOpen={() => {
        setCheckoutValidations({
          ...checkoutValidations,
          cartLoginPopup: {
            isValid: true,
          },
        });
        setDialog(null);
      }}
      translateAxis="y"
      customClass="overflow-scroll md:!top-auto md:absolute rounded-t-2xl max-h-[81vh]"
      dialogOverlay={true}
      onOpenAutoFocus={(e) => {
        // Focus the phone number <input/> (opens the keyboard on mobile) instead of letting Radix
        // focus the first tabbable element, which is the hidden country <select> — on iOS Safari
        // that opens the country picker wheel.
        e.preventDefault();
        (e.currentTarget as HTMLElement | null)
          ?.querySelector<HTMLInputElement>("#flo__auth__phoneInput")
          ?.focus();
      }}
      closeOnOverlayClick={!isOverlayClickDisabled}>
      <DialogHeader className={`${isOverlayClickDisabled ? "!px-3" : "px-4"}`}>
        <div className={`flex h-full space-y-0.5 w-full bg-white py-4`}>
          <div className="flex flex-col w-full items-start">
            <h2 className="inline-flex gap-2 text-lg font-semibold text-carbon-dark">{t("almost_there")}</h2>
            <p className="text-sm font-normal text-coal-light">
              {checkoutValidations?.cartLoginPopup?.isValid
                ? t("verify_phone_number_to_place_order")
                : checkoutValidations?.cartLoginPopup?.metadata?.message}
            </p>
          </div>
          {isOverlayClickDisabled && (
            <button className="outline-none">
              <X
                className="h-6 w-6 cursor-pointer text-carbon-dark"
                onClick={() => {
                  setDialog(null);
                }}
              />
            </button>
          )}
        </div>
      </DialogHeader>
      <DialogBody className={`flex h-96 flex-col`}>
        <div className="flex w-full flex-row items-center justify-center p-3">
          <form
            onSubmit={handleSubmit(handlePhoneNumberSubmit)}
            className="flex w-full flex-col items-center gap-3">
            <PhoneInput
              defaultCountry="IN"
              {...inputProps(FormField.phoneNumber, {
                onFocus: () => onPhoneInputFocus(),
                onBlur: () => setPhoneInputFocused(false),
                onChange: (value: PhoneInputValueType) => {
                  handleFieldChange(FormField.phoneNumber, value ?? "");
                },
              })}
              onCountryChange={(code) => setSelectedCountryCode(code)}
              numberInputProps={{
                maxLength: getPhoneInputMaxLength(values?.phoneNumber, selectedCountryCode),
              }}
              value={user?.phone ?? ""}
              countries={merchant?.addressConfig?.enabledCountries}
              id="flo__auth__phoneInput"
              smartCaret={false}
              className={`placeholder-sm peer flex h-[3.25rem] w-full appearance-none rounded-xl border border-gray-light px-3 text-base placeholder-transparent
                ${errors.phoneNumber?.status && "border-ouch"}
                ${
                  Boolean(values?.phoneNumber?.length) && !errors?.phoneNumber?.status
                    ? "border-[#D5E7DB]"
                    : ""
                } ${
                phoneInputFocused &&
                "border-[1px] !border-primary-dark px-[12px] outline-none ring-[2px] ring-primary-light"
              }`}
              placeholder={t("phone_number")}
              international={true}
              countryCallingCodeEditable={false}
              autoFocus={false}
              limitMaxLength
              error={errors.phoneNumber?.message}
            />
            {errors.phoneNumber?.status && (
              <p
                className={`mt-1 w-full px-3 text-center font-normal ${
                  errors.phoneNumber?.status ? "self-start text-sm text-ouch" : "text-xs text-coal-light"
                }`}>
                {errors.phoneNumber?.status && errors.phoneNumber?.message}
              </p>
            )}
            <div className="w-full flex flex-col gap-2">
              <CustomComponentSlot position="AUTH_PHONE_SECTION" />
              <DPDPAConsent className="px-2" />
            </div>
            <PrimaryButton
              buttonText={t("verify_otp")}
              isLoading={isLoading || isCaptchaValidating}
              isDisabled={isCtaBlockedByRule}
              id="flo__auth__loginButton"
              type="submit"
              className={classNames(
                captchaToken.status === CaptchaStatus.ERROR && isCaptchaValidating ? "!hidden" : "block",
              )}
            />

            <CaptchaWrapper
              context="CART_AUTHENTICATION_DIALOG"
              onTokenChange={setCaptchaToken}
              status={captchaToken.status}
            />

            <MarketingConsent />

            <div className="flex justify-center items-center">
              <ShopfloBadge />
            </div>
          </form>
        </div>
      </DialogBody>
    </GenericDialog>
  );
};

export default CartAuthenticationDialog;
